import { useState, useCallback, useEffect, useMemo } from 'react';
import type {
  HabitWithStreak,
  HabitCompletion,
  CreateHabitInput,
  UpdateHabitInput,
  CoachingCategory,
} from '@/types';
import {
  getHabits,
  getHabitsByCategory,
  createHabit as apiCreateHabit,
  updateHabit as apiUpdateHabit,
  deleteHabit as apiDeleteHabit,
  archiveHabit as apiArchiveHabit,
  completeHabit as apiCompleteHabit,
  subscribeToCompletions,
} from '@/lib/habits-api';

interface UseHabitsOptions {
  filterCategory?: CoachingCategory | null;
}

interface UseHabitsReturn {
  habits: HabitWithStreak[];
  isLoading: boolean;
  error: string | null;
  selectedCategory: CoachingCategory | null;
  setSelectedCategory: (category: CoachingCategory | null) => void;
  createHabit: (input: CreateHabitInput) => Promise<void>;
  updateHabit: (id: string, updates: UpdateHabitInput) => Promise<void>;
  deleteHabit: (habitId: string) => Promise<void>;
  archiveHabit: (habitId: string) => Promise<void>;
  completeHabit: (habitId: string, notes?: string, moodRating?: number) => Promise<HabitCompletion>;
  refreshHabits: () => Promise<void>;
  // Computed values
  todayProgress: {
    completed: number;
    total: number;
    percentage: number;
  };
  habitsByCategory: Record<CoachingCategory, HabitWithStreak[]>;
}

export function useHabits(options: UseHabitsOptions = {}): UseHabitsReturn {
  const [habits, setHabits] = useState<HabitWithStreak[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<CoachingCategory | null>(
    options.filterCategory ?? null
  );

  // Fetch habits
  const fetchHabits = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = selectedCategory !== null
        ? await getHabitsByCategory(selectedCategory)
        : await getHabits();
      setHabits(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch habits');
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory]);

  // Load habits on mount and when category changes
  useEffect(() => {
    fetchHabits();
  }, [fetchHabits]);

  // Subscribe to realtime completion updates
  useEffect(() => {
    const unsubscribe = subscribeToCompletions('current-user', (completion) => {
      // Update the habit's completedToday status when a completion comes in
      setHabits((prev) =>
        prev.map((habit) =>
          habit.id === completion.habitId
            ? {
                ...habit,
                completedToday: true,
                currentStreak: completion.streakAtCompletion,
              }
            : habit
        )
      );
    });

    return unsubscribe;
  }, []);

  // Create habit
  const createHabit = useCallback(async (input: CreateHabitInput) => {
    try {
      await apiCreateHabit(input);
      await fetchHabits(); // Refresh to get updated list with streak data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create habit');
      throw err;
    }
  }, [fetchHabits]);

  // Update habit
  const updateHabit = useCallback(async (id: string, updates: UpdateHabitInput) => {
    try {
      const updated = await apiUpdateHabit(id, updates);
      setHabits((prev) =>
        prev.map((h) => (h.id === id ? { ...h, ...updated } : h))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update habit');
      throw err;
    }
  }, []);

  // Delete habit
  const deleteHabit = useCallback(async (habitId: string) => {
    try {
      await apiDeleteHabit(habitId);
      setHabits((prev) => prev.filter((h) => h.id !== habitId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete habit');
      throw err;
    }
  }, []);

  // Archive habit
  const archiveHabit = useCallback(async (habitId: string) => {
    try {
      await apiArchiveHabit(habitId);
      setHabits((prev) => prev.filter((h) => h.id !== habitId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to archive habit');
      throw err;
    }
  }, []);

  // Complete habit
  const completeHabit = useCallback(async (
    habitId: string,
    notes?: string,
    moodRating?: number
  ): Promise<HabitCompletion> => {
    try {
      const completion = await apiCompleteHabit(habitId, notes, moodRating);
      // Optimistically update the habit (realtime will also update)
      setHabits((prev) =>
        prev.map((h) =>
          h.id === habitId
            ? {
                ...h,
                completedToday: true,
                currentStreak: completion.streakAtCompletion,
              }
            : h
        )
      );
      return completion;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete habit');
      throw err;
    }
  }, []);

  // Computed: Today's progress
  const todayProgress = useMemo(() => {
    const dailyHabits = habits.filter((h) => h.frequency === 'daily');
    const completed = dailyHabits.filter((h) => h.completedToday).length;
    const total = dailyHabits.length;
    return {
      completed,
      total,
      percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [habits]);

  // Computed: Habits grouped by category
  const habitsByCategory = useMemo(() => {
    const grouped: Record<CoachingCategory, HabitWithStreak[]> = {
      faith: [],
      family: [],
      finance: [],
      fitness: [],
    };
    habits.forEach((habit) => {
      grouped[habit.category].push(habit);
    });
    return grouped;
  }, [habits]);

  return {
    habits,
    isLoading,
    error,
    selectedCategory,
    setSelectedCategory,
    createHabit,
    updateHabit,
    deleteHabit,
    archiveHabit,
    completeHabit,
    refreshHabits: fetchHabits,
    todayProgress,
    habitsByCategory,
  };
}
